"use client"

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { WidgetBase, WidgetBaseProps } from '../core/widget-base'
import { 
  Server,
  Cpu,
  HardDrive,
  MemoryStick,
  Network,
  Activity,
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Thermometer,
  Wifi,
  WifiOff,
  Database,
  Globe,
  Shield,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  BarChart3
} from 'lucide-react'
import { Badge } from '../../ui/badge'
import { Progress } from '../../ui/progress'
import { Card } from '../../ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip'
import { useSubscription } from '../../../hooks/use-subscription'
import { ProLockScreen } from '../../shared/pro-lock-screen'

export interface ServerMetric {
  label: string
  value: number | string
  unit?: string
  max?: number
  status?: 'healthy' | 'warning' | 'critical' | 'offline'
  trend?: 'up' | 'down' | 'stable'
  icon?: React.ReactNode
  history?: number[]
}

export interface ServerData {
  id: string
  name: string
  location?: string
  type?: 'web' | 'database' | 'cache' | 'storage' | 'compute'
  status: 'online' | 'offline' | 'maintenance' | 'degraded'
  uptime?: number // in percentage
  lastUpdated?: Date | string
  metrics: {
    cpu?: ServerMetric
    memory?: ServerMetric
    disk?: ServerMetric
    network?: ServerMetric
    temperature?: ServerMetric
    requests?: ServerMetric
    responseTime?: ServerMetric
    errorRate?: ServerMetric
    custom?: ServerMetric[]
  }
  alerts?: Array<{
    id: string
    type: 'warning' | 'error' | 'info'
    message: string
    timestamp: Date | string
  }>
  services?: Array<{
    name: string
    status: 'running' | 'stopped' | 'error'
    port?: number
    memory?: number
    cpu?: number
  }>
}

export interface ServerMonitorWidgetProps extends Omit<Partial<WidgetBaseProps>, 'variant'> {
  data: ServerData | ServerData[]
  title?: string
  variant?: 'default' | 'compact' | 'detailed' | 'grid'
  backgroundVariant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  gradientDirection?: 'to-t' | 'to-tr' | 'to-r' | 'to-br' | 'to-b' | 'to-bl' | 'to-l' | 'to-tl'
  showMetrics?: boolean
  showAlerts?: boolean
  showServices?: boolean
  showHistory?: boolean
  showUptime?: boolean
  updateInterval?: number // in seconds
  animate?: boolean

  onServerClick?: (server: ServerData) => void
  onAlertClick?: (alert: any) => void
}

export function ServerMonitorWidget({
  data,
  title,
  variant = 'default',
  backgroundVariant,
  gradientDirection,
  showMetrics = true,
  showAlerts = true,
  showServices = false,
  showHistory = false,
  showUptime = true,
  updateInterval = 30,
  animate = true,

  onServerClick,
  onAlertClick,
  ...widgetProps
}: ServerMonitorWidgetProps) {
  const { hasProAccess, isLoading } = useSubscription();

  const servers = Array.isArray(data) ? data : [data]
  const [selectedTab, setSelectedTab] = React.useState('overview')
  const [liveData, setLiveData] = React.useState(servers)

  // Format number with max 2 decimal places
  const formatNumber = (value: number | string): string => {
    if (typeof value === 'string') return value
    if (value % 1 !== 0) {
      return (Math.round(value * 100) / 100).toString()
    }
    return value.toString()
  }

  // Simulate live updates
  React.useEffect(() => {
    if (!animate) return

    const interval = setInterval(() => {
      setLiveData(prevData => 
        prevData.map(server => ({
          ...server,
          lastUpdated: new Date(),
          metrics: {
            ...server.metrics,
            cpu: server.metrics.cpu ? {
              ...server.metrics.cpu,
              value: Math.min(100, Math.max(0, (server.metrics.cpu.value as number) + (Math.random() - 0.5) * 10))
            } : undefined,
            memory: server.metrics.memory ? {
              ...server.metrics.memory,
              value: Math.min(100, Math.max(0, (server.metrics.memory.value as number) + (Math.random() - 0.5) * 5))
            } : undefined
          }
        }))
      )
    }, updateInterval * 1000)

    return () => clearInterval(interval)
  }, [updateInterval, animate])

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'running':
        return 'text-green-600 dark:text-green-400'
      case 'warning':
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'critical':
      case 'error':
      case 'offline':
      case 'stopped':
        return 'text-red-600 dark:text-red-400'
      case 'maintenance':
        return 'text-blue-600 dark:text-blue-400'
      default:
        return 'text-gray-600 dark:text-gray-400'
    }
  }

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'running':
        return <CheckCircle className="h-4 w-4" />
      case 'warning':
      case 'degraded':
        return <AlertTriangle className="h-4 w-4" />
      case 'critical':
      case 'error':
      case 'offline':
      case 'stopped':
        return <XCircle className="h-4 w-4" />
      case 'maintenance':
        return <Clock className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  // Get server type icon
  const getServerIcon = (type?: string) => {
    switch (type) {
      case 'database':
        return <Database className="h-5 w-5" />
      case 'cache':
        return <MemoryStick className="h-5 w-5" />
      case 'storage':
        return <HardDrive className="h-5 w-5" />
      case 'compute':
        return <Cpu className="h-5 w-5" />
      case 'web':
      default:
        return <Server className="h-5 w-5" />
    }
  }

  // Get metric status
  const getMetricStatus = (metric: ServerMetric): 'healthy' | 'warning' | 'critical' => {
    if (metric.status) return metric.status as any

    const value = metric.value as number
    const max = metric.max || 100
    const percentage = (value / max) * 100

    if (percentage >= 90) return 'critical'
    if (percentage >= 75) return 'warning'
    return 'healthy'
  }

  // Render metric bar
  const renderMetricBar = (metric: ServerMetric, icon: React.ReactNode) => {
    const value = metric.value as number
    const max = metric.max || 100
    const percentage = (value / max) * 100
    const status = getMetricStatus(metric)

    const statusColors = {
      healthy: 'bg-green-600',
      warning: 'bg-yellow-600',
      critical: 'bg-red-600',
      offline: 'bg-gray-600'
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-muted-foreground">{icon}</div>
            <span className="text-sm font-medium">{metric.label}</span>
            {metric.trend && (
              <div className={cn(
                "text-xs",
                metric.trend === 'up' ? 'text-green-600' : 
                metric.trend === 'down' ? 'text-red-600' : 
                'text-gray-600'
              )}>
                {metric.trend === 'up' ? <TrendingUp className="h-3 w-3" /> : 
                 metric.trend === 'down' ? <TrendingDown className="h-3 w-3" /> : 
                 null}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">
              {formatNumber(value)}{metric.unit || '%'}
            </span>
            {max && max !== 100 && (
              <span className="text-xs text-muted-foreground">
                / {formatNumber(max)}{metric.unit}
              </span>
            )}
          </div>
        </div>
        <div className="relative">
          <Progress 
            value={percentage} 
            className="h-2"
          />
          <motion.div
            className={cn(
              "absolute top-0 left-0 h-2 rounded-full",
              statusColors[status]
            )}
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: animate ? 0.5 : 0 }}
          />
        </div>
        {showHistory && metric.history && metric.history.length > 0 && (
          <div className="flex items-end gap-0.5 h-8">
            {metric.history.slice(-20).map((val, idx) => (
              <motion.div
                key={idx}
                className={cn(
                  "flex-1 rounded-t",
                  val > 75 ? 'bg-red-600/50' : 
                  val > 50 ? 'bg-yellow-600/50' : 
                  'bg-green-600/50'
                )}
                initial={{ height: 0 }}
                animate={{ height: `${(val / 100) * 32}px` }}
                transition={{ delay: idx * 0.01 }}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Render server card
  const renderServerCard = (server: ServerData) => {
    const isOnline = server.status === 'online'
    const metrics = server.metrics

    return (
      <Card
        className={cn(
          "p-4 transition-all",
          onServerClick && "cursor-pointer hover:shadow-lg",
          !isOnline && "opacity-75"
        )}
        onClick={() => onServerClick?.(server)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10"
            )}>
              {getServerIcon(server.type)}
            </div>
            <div>
              <h4 className="font-semibold">{server.name}</h4>
              {server.location && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {server.location}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant={isOnline ? "secondary" : "destructive"}
              leftIcon={isOnline ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            >
              {server.status}
            </Badge>
            {isOnline ? (
              <Wifi className="h-4 w-4 text-green-600" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-600" />
            )}
          </div>
        </div>

        {/* Uptime */}
        {showUptime && server.uptime !== undefined && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Uptime</span>
              <span className="font-medium">{formatNumber(server.uptime)}%</span>
            </div>
            <Progress value={server.uptime} className="h-1" />
          </div>
        )}

        {/* Metrics */}
        {showMetrics && (
          <div className="space-y-3">
            {metrics.cpu && renderMetricBar(metrics.cpu, <Cpu className="h-4 w-4" />)}
            {metrics.memory && renderMetricBar(metrics.memory, <MemoryStick className="h-4 w-4" />)}
            {metrics.disk && renderMetricBar(metrics.disk, <HardDrive className="h-4 w-4" />)}
            {metrics.network && renderMetricBar(metrics.network, <Network className="h-4 w-4" />)}
            {metrics.temperature && renderMetricBar(metrics.temperature, <Thermometer className="h-4 w-4" />)}
          </div>
        )}

        {/* Additional metrics */}
        {metrics.requests || metrics.responseTime || metrics.errorRate ? (
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t">
            {metrics.requests && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Requests</p>
                <p className="text-sm font-semibold">{formatNumber(metrics.requests.value as number)}/s</p>
              </div>
            )}
            {metrics.responseTime && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Response</p>
                <p className="text-sm font-semibold">{formatNumber(metrics.responseTime.value as number)}ms</p>
              </div>
            )}
            {metrics.errorRate && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Errors</p>
                <p className="text-sm font-semibold">{formatNumber(metrics.errorRate.value as number)}%</p>
              </div>
            )}
          </div>
        ) : null}

        {/* Alerts */}
        {showAlerts && server.alerts && server.alerts.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Recent Alerts</span>
              <Badge variant="outline" className="text-xs">
                {server.alerts.length}
              </Badge>
            </div>
            <div className="space-y-1">
              {server.alerts.slice(0, 3).map(alert => (
                <div
                  key={alert.id}
                  className={cn(
                    "text-xs p-2 rounded flex items-start gap-2",
                    alert.type === 'error' ? 'bg-red-100 dark:bg-red-900/20' :
                    alert.type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/20' :
                    'bg-blue-100 dark:bg-blue-900/20',
                    onAlertClick && "cursor-pointer hover:opacity-80"
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAlertClick?.(alert)
                  }}
                >
                  {alert.type === 'error' ? <XCircle className="h-3 w-3 text-red-600 flex-shrink-0" /> :
                   alert.type === 'warning' ? <AlertTriangle className="h-3 w-3 text-yellow-600 flex-shrink-0" /> :
                   <AlertCircle className="h-3 w-3 text-blue-600 flex-shrink-0" />
                  }
                  <span className="flex-1">{alert.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Last updated */}
        {server.lastUpdated && (
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
            <span>Last updated</span>
            <span>
              {new Date(server.lastUpdated).toLocaleTimeString()}
            </span>
          </div>
        )}
      </Card>
    )
  }

  // Render compact view
  const renderCompactView = () => {
    return (
      <div className="space-y-2">
        {liveData.map((server, index) => (
          <motion.div
            key={server.id}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg border",
              onServerClick && "cursor-pointer hover:bg-muted/30 transition-colors"
            )}
            onClick={() => onServerClick?.(server)}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <div className="flex items-center gap-3">
              {getServerIcon(server.type)}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{server.name}</span>
                  <Badge 
                    variant={server.status === 'online' ? "secondary" : "destructive"}
                    className="text-xs"
                    leftIcon={server.status === 'online' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                  >
                    {server.status}
                  </Badge>
                </div>
                {server.location && (
                  <p className="text-xs text-muted-foreground">{server.location}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {server.metrics.cpu && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">CPU</p>
                  <p className="text-sm font-semibold">{formatNumber(server.metrics.cpu.value as number)}%</p>
                </div>
              )}
              {server.metrics.memory && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Memory</p>
                  <p className="text-sm font-semibold">
                    {formatNumber(server.metrics.memory.value as number)}
                    {server.metrics.memory.unit || '%'}
                  </p>
                </div>
              )}
              {showUptime && server.uptime !== undefined && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Uptime</p>
                  <p className="text-sm font-semibold">{formatNumber(server.uptime)}%</p>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    )
  }

  // Render based on variant
  const renderContent = () => {
    switch (variant) {
      case 'compact':
        return renderCompactView()
      
      case 'grid':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {liveData.map(server => (
              <div key={server.id}>
                {renderServerCard(server)}
              </div>
            ))}
          </div>
        )
      
      case 'detailed':
        return (
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="metrics">Metrics</TabsTrigger>
              <TabsTrigger value="alerts">Alerts</TabsTrigger>
            </TabsList>
            {liveData.map(server => (
              <div key={server.id}>
                <TabsContent value="overview">
                  {renderServerCard(server)}
                </TabsContent>
                <TabsContent value="metrics" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(server.metrics).map(([key, metric]) => {
                      if (!metric || key === 'custom' || Array.isArray(metric)) return null
                      return (
                        <Card key={key} className="p-4">
                          {renderMetricBar(
                            metric as ServerMetric,
                            key === 'cpu' ? <Cpu className="h-4 w-4" /> :
                            key === 'memory' ? <MemoryStick className="h-4 w-4" /> :
                            key === 'disk' ? <HardDrive className="h-4 w-4" /> :
                            key === 'network' ? <Network className="h-4 w-4" /> :
                            <Activity className="h-4 w-4" />
                          )}
                        </Card>
                      )
                    })}
                  </div>
                </TabsContent>
                <TabsContent value="alerts">
                  {server.alerts && server.alerts.length > 0 ? (
                    <div className="space-y-2">
                      {server.alerts.map(alert => (
                        <div
                          key={alert.id}
                          className={cn(
                            "p-3 rounded-lg flex items-start gap-3",
                            alert.type === 'error' ? 'bg-red-100 dark:bg-red-900/20' :
                            alert.type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/20' :
                            'bg-blue-100 dark:bg-blue-900/20',
                            onAlertClick && "cursor-pointer hover:opacity-80"
                          )}
                          onClick={() => onAlertClick?.(alert)}
                        >
                          {alert.type === 'error' ? <XCircle className="h-5 w-5 text-red-600" /> :
                           alert.type === 'warning' ? <AlertTriangle className="h-5 w-5 text-yellow-600" /> :
                           <AlertCircle className="h-5 w-5 text-blue-600" />
                          }
                          <div className="flex-1">
                            <p className="font-medium">{alert.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(alert.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No active alerts</p>
                    </div>
                  )}
                </TabsContent>
              </div>
            ))}
          </Tabs>
        )
      
      case 'default':
      default:
        return (
          <div className="space-y-4">
            {liveData.map(server => (
              <div key={server.id}>
                {renderServerCard(server)}
              </div>
            ))}
          </div>
        )
    }
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="Server Monitor Widget" 
        compact={true}
      />
    );
  }

  return (
    <WidgetBase
      title={title}
      variant={backgroundVariant}
      gradientDirection={gradientDirection}
      {...widgetProps}
    >
      <AnimatePresence mode="wait">
        {renderContent()}
      </AnimatePresence>
    </WidgetBase>
  )
}

export default ServerMonitorWidget